import { useMutation, useQuery } from "@apollo/client";
import {
  ChevronDownIcon,
  PauseIcon,
  PlayIcon,
  XCircleIcon,
} from "@heroicons/react/outline";
import moment from "moment";
import { useEffect, useState } from "react";
import {
  AddBotToMeetingNewPageMutationMutation,
  AddBotToMeetingNewPageMutationMutationVariables,
  ChangeBotRecordingStatusNewPageMutationMutation,
  ChangeBotRecordingStatusNewPageMutationMutationVariables,
  GetMeetingBotStatusQueryQuery,
  GetMeetingBotStatusQueryQueryVariables,
  GetMeetingTranscriptQueryQuery,
  MeetingViewMeetingNodeNewPageFragmentFragment,
  OnlineVideoConference,
  RemoveBotFromMeetingNewPageMutationMutation,
  RemoveBotFromMeetingNewPageMutationMutationVariables,
} from "types/graphql-schema";

import addBotToMeetingMutation from "@apps/meeting-new/graphql/add-bot-to-meeting-mutation";
import changeBotRecordingStatusMutation from "@apps/meeting-new/graphql/change-bot-recording-status-mutation";
import removeBotFromMeetingMutation from "@apps/meeting-new/graphql/remove-bot-from-meeting-mutation";
import Button, { buttonTheme } from "@components/button/button";
import Dropdown from "@components/dropdown/dropdown";
import Loading from "@components/loading/loading";
import Tooltip from "@components/tooltip/tooltip";
import { videoConferenceName } from "@helpers/constants";
import { classNames } from "@helpers/css";
import { removeCurrentYear } from "@helpers/helpers";
import useConfirm from "@helpers/hooks/use-confirm";
import { isPageHidden } from "@helpers/hooks/use-page-visible";
import { titleCase } from "@helpers/string";

import getMeetingBotStatusQuery from "../graphql/get-meeting-bot-status-query";

const pollInterval = 20 * 1000; // fetch every 15s

export const MeetingBot = ({
  meeting,
  compact = false,
  statusClassName,
}: {
  meeting:
    | MeetingViewMeetingNodeNewPageFragmentFragment
    | NonNullable<GetMeetingTranscriptQueryQuery["meeting"]>;
  compact?: boolean;
  statusClassName?: string;
}) => {
  const meetingId = meeting.id;
  const [localAddBotToMeetingLoading, setLocalAddBotToMeetingLoading] =
    useState(false);
  const [
    localChangeBotRecordingStatusLoading,
    setLocalChangeBotRecordingStatusLoading,
  ] = useState(false);
  const [
    localRemoveBotFromMeetingLoading,
    setLocalRemoveBotFromMeetingLoading,
  ] = useState(false);

  const [addBotToMeeting, { loading: addBotToMeetingLoading }] = useMutation<
    AddBotToMeetingNewPageMutationMutation,
    AddBotToMeetingNewPageMutationMutationVariables
  >(addBotToMeetingMutation);
  const [removeBotFromMeeting, { loading: removeBotFromMeetingLoading }] =
    useMutation<
      RemoveBotFromMeetingNewPageMutationMutation,
      RemoveBotFromMeetingNewPageMutationMutationVariables
    >(removeBotFromMeetingMutation);
  const [
    changeBotRecordingStatus,
    { loading: changeBotRecordingStatusLoading },
  ] = useMutation<
    ChangeBotRecordingStatusNewPageMutationMutation,
    ChangeBotRecordingStatusNewPageMutationMutationVariables
  >(changeBotRecordingStatusMutation);

  const { hasBotInMeeting, botIsRecording, botIsWaitingToJoinMeeting } =
    meeting;

  const loadingUI =
    botIsWaitingToJoinMeeting ||
    // local loading
    localAddBotToMeetingLoading ||
    localChangeBotRecordingStatusLoading ||
    localRemoveBotFromMeetingLoading ||
    // mutation loading
    addBotToMeetingLoading ||
    changeBotRecordingStatusLoading ||
    removeBotFromMeetingLoading;

  // The meeting bot states will be updated and propagated by pusher but just in case
  // we refresh (with the query below) the meeting bot states regularly when showing the loading UI.
  // The mutations do not return the actual bot state, recall api calls are done asynchronously.
  useQuery<
    GetMeetingBotStatusQueryQuery,
    GetMeetingBotStatusQueryQueryVariables
  >(getMeetingBotStatusQuery, {
    skip: !loadingUI,
    skipPollAttempt: isPageHidden,
    pollInterval,
    variables: { meetingId },
  });

  const { confirm, ConfirmationDialog } = useConfirm(
    "Are you sure you want to remove the Notetaker?",
    "Removing the Notetaker will stop Topicflow from recording notes used for creating the meeting recap."
  );

  // Reset loading state when bot status changes
  useEffect(() => {
    if (localAddBotToMeetingLoading) {
      setLocalAddBotToMeetingLoading(false);
    }
    if (localChangeBotRecordingStatusLoading) {
      setLocalChangeBotRecordingStatusLoading(false);
    }
    if (localRemoveBotFromMeetingLoading) {
      setLocalRemoveBotFromMeetingLoading(false);
    }
  }, [botIsRecording, botIsWaitingToJoinMeeting, hasBotInMeeting]);

  const willJoinAtTime =
    meeting.meetingGroup?.addTranscriptionBotToMeetingsByDefault &&
    meeting.startDatetime &&
    moment().isBefore(meeting.startDatetime)
      ? meeting.startDatetime
      : null;
  const willJoinToday =
    willJoinAtTime && moment(willJoinAtTime).isSame(moment(), "day");
  const conferenceVideoName = meeting.videoConferenceType
    ? videoConferenceName[meeting.videoConferenceType]
    : "None";

  const text =
    addBotToMeetingLoading || botIsWaitingToJoinMeeting
      ? "Waiting to join"
      : (localChangeBotRecordingStatusLoading ||
          changeBotRecordingStatusLoading) &&
        !botIsRecording
      ? "Resuming"
      : (localChangeBotRecordingStatusLoading ||
          changeBotRecordingStatusLoading) &&
        botIsRecording
      ? "Pausing"
      : localRemoveBotFromMeetingLoading || removeBotFromMeetingLoading
      ? "Removing"
      : botIsRecording
      ? "Notetaker active"
      : hasBotInMeeting
      ? "Notetaker paused"
      : willJoinAtTime
      ? `Notetaker will join${
          willJoinToday
            ? " "
            : ` on ${removeCurrentYear(moment(willJoinAtTime).format("ll"))}`
        } at ${moment(willJoinAtTime).format("H:mma").replace(":00", "")}`
      : `Notetaker not in ${titleCase(conferenceVideoName)}`;

  const handleClickAddBotInMeeting = () => {
    setLocalAddBotToMeetingLoading(true);
    addBotToMeeting({
      variables: { meetingId },
    });
  };

  const handleClickRemoveBotInMeeting = async () => {
    const confirmed = await confirm();
    if (confirmed) {
      setLocalRemoveBotFromMeetingLoading(true);
      removeBotFromMeeting({
        variables: { meetingId },
      });
    }
  };

  const handleClickPauseBotRecording = () => {
    setLocalChangeBotRecordingStatusLoading(true);
    const isRecording = !botIsRecording;
    changeBotRecordingStatus({
      variables: { meetingId, isRecording },
    });
  };

  const meetingGroup = meeting.meetingGroup;
  if (!meetingGroup) return null;
  if (meeting.draft) return null;
  if (
    !meeting.videoConferenceType ||
    meeting.videoConferenceType === OnlineVideoConference.None
  )
    return null;

  return (
    <>
      {/* Status */}
      <Tooltip text={text}>
        <div
          className={classNames(
            "flex items-center gap-1 text-sm tracking-tight text-gray-500",
            statusClassName
          )}
        >
          <div className="flex items-center justify-center h-4 w-4 flex-none">
            {loadingUI ? (
              <Loading size={4} mini />
            ) : (
              <span
                className={classNames(
                  "h-2 w-2 rounded-full shrink-0",
                  meeting.botIsRecording
                    ? "bg-red-500 drop-shadow-[0_0px_4px_rgba(239,68,68,0.25)] animate-pulse"
                    : "bg-gray-300"
                )}
              />
            )}
          </div>
          <span className="min-w-0 truncate">{text}</span>
        </div>
      </Tooltip>

      {/* Buttons */}
      {hasBotInMeeting ? (
        <div className="flex items-center">
          <Button
            className="border-r-0 rounded-r-none"
            onClick={handleClickPauseBotRecording}
            disabled={loadingUI}
          >
            {botIsRecording ? (
              <PauseIcon className="h-5 w-5 text-gray-500" />
            ) : (
              <PlayIcon className="h-5 w-5 text-gray-500" />
            )}
            <span className="text-nowrap">
              {botIsRecording ? "Pause" : "Resume"}
              {compact ? "" : " Notetaker"}
            </span>
          </Button>
          <Dropdown
            align="end"
            disabled={loadingUI}
            options={[
              {
                label: (
                  <span className="-ml-1 flex items-center gap-1 text-nowrap">
                    {!botIsRecording ? (
                      <PauseIcon className="h-4 w-4 text-gray-500" />
                    ) : (
                      <PlayIcon className="h-4 w-4 text-gray-500" />
                    )}
                    {!botIsRecording ? "Pause Notetaker" : "Resume Notetaker"}
                  </span>
                ),
                disabled: true,
                onClick: handleClickPauseBotRecording,
              },
              {
                label: (
                  <span className="-ml-1 flex items-center gap-1">
                    <XCircleIcon className="h-4 w-4 text-red-500" />
                    Remove Notetaker
                  </span>
                ),
                onClick: handleClickRemoveBotInMeeting,
              },
            ]}
          >
            <Button className="rounded-l-none px-1.5">
              <ChevronDownIcon className="h-4 w-4 text-gray-500" />
            </Button>
          </Dropdown>
          <ConfirmationDialog />
        </div>
      ) : !hasBotInMeeting ? (
        <Button
          theme={buttonTheme.primary}
          className="text-nowrap"
          text="Add Notetaker"
          disabled={loadingUI}
          onClick={handleClickAddBotInMeeting}
        />
      ) : null}
    </>
  );
};

export default MeetingBot;
